import { useMemo } from 'react';

import {
  CLIENT_ID,
  CONTAINER_ID,
  TITLE_ID,
} from '~/components/shared/recommendations/stylitics/constants';
import { setJumpLinkState } from '~/components/shared/recommendations/stylitics/jumpLinkStore';
import type {
  StyliticsClickItemData,
  StyliticsLoadComposerData,
  StyliticsMountOutfitData,
  StyliticsConfiguration,
} from '~/components/shared/recommendations/stylitics/types';
import { useCookieContext } from '~/utilities/context/dynamic/CookieContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useAppContext } from '~/utilities/context/static/AppContext';

const nextOutfitPeekMap = new Map<string, number>([
  ['mobile', 0.15],
  ['tablet', 0.2],
  ['desktop', 0.15],
]);

export const useStyliticsConfiguration = (
  productCode: string,
  onMountOutfit: (data: StyliticsMountOutfitData) => void,
  onClickItem: (data: StyliticsClickItemData) => void,
  title?: string,
  jumpLink?: string
): StyliticsConfiguration => {
  const { browserLocale, isProduction } = useAppContext();
  const { che: clientUserId } = useCookieContext();
  const { breakpoint = 'desktop' } = useMediaQueryContext();

  return useMemo(
    () => {
      const env = isProduction ? 'production' : 'staging';

      return {
        client: CLIENT_ID,
        target: CONTAINER_ID,
        config: {
          modules: [
            {
              type: 'hotspots',
              params: {
                api: {
                  item_number: productCode,
                  min: 1,
                  env,
                },
                text: {
                  moduleHeading: title,
                  moduleHeadingLevel: 'h2',
                  moduleHeadingId: TITLE_ID,
                },
                display: {
                  gutterWidth: 4,
                  cardAspectRatio: 1,
                  nextOutfitPeek: nextOutfitPeekMap.get(breakpoint) || 0.15,
                  desktopHorizontalScroll: true,
                  showMobileArrows: false,
                },
                responsive: [
                  [0, { columns: 1 }],
                  [768, { columns: 2 }],
                  [1024, { columns: 3 }],
                ],
              },
              on: {
                'mount.outfit': onMountOutfit,
              },
              override: {
                'click.item': onClickItem,
                // A no-op function to override the default behavior.
                'click.outfit': () => {},
              },
            },
          ],
          on: {
            'load.composer': (data: StyliticsLoadComposerData) => {
              setJumpLinkState({ label: jumpLink, hidden: !!data?.hidden });
            },
          },
          sharedParams: {
            customer: {
              clientUserId,
              locale: browserLocale,
            },
            analytics: {
              placement: 'pdp',
            },
          },
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productCode, title, jumpLink, onMountOutfit, onClickItem]
  );
};
